import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";
import {
  EditOutlined,
  OpenInNewOutlined,
  PushPin,
  PushPinOutlined,
} from "@mui/icons-material";
import { CircularProgress, InputBase } from "@mui/material";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Flx from "../../../components/layout/Flx";
import { useWorkspaceTabs } from "../../../hooks/utils/useWorkspaceTabs";
import { PIPELINES, PIPELINE_KEYS } from "../../../pipelines/pipelineConfig";

// A pinned tab records the pipeline its loan belongs to. Tabs pinned before
// that field existed have no `pipeline`, so fall back to underwriting (/loan).
const pipelineOf = (pipeline) =>
  PIPELINES[pipeline] ? pipeline : PIPELINE_KEYS.UNDERWRITING;
const drilldownBaseFor = (pipeline) =>
  PIPELINES[pipelineOf(pipeline)].drilldownBase;

// Browser-chrome palette: the strip reads as a continuation of the blue brand
// header, with the active tab lifted out of it in white.
const STRIP_BG = "#1B50D8"; // one step darker than the header's #1F5AF2
const BRAND = "#1F5AF2";
const TAB_IDLE_BG = "rgba(255,255,255,0.13)";
const TAB_IDLE_HOVER_BG = "rgba(255,255,255,0.22)";

// ============================================================
// WorkspaceTabs — pinned-loan strip (persisted to user prefs)
// ============================================================
// Presentational pinned-loan strip. Visibility is controlled by the caller
// (the header's pinned-tabs toggle) — this just renders the tabs, or nothing
// when there are none pinned.
const WorkspaceTabs = ({ onToggle }) => {
  const { tabs, removeTab, renameTab, reorderTab, loading } =
    useWorkspaceTabs();

  // Drag-to-reorder state: the tab being dragged and the one it's hovering over
  // (drives the insertion indicator). Native HTML5 DnD — no extra dependency.
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDragStart = useCallback((loanId) => setDraggingId(loanId), []);
  const handleDragOverTab = useCallback((loanId) => setDragOverId(loanId), []);
  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverId(null);
  }, []);
  const handleDropOnTab = useCallback(
    (targetId) => {
      if (draggingId && targetId && draggingId !== targetId) {
        reorderTab(draggingId, targetId);
      }
      handleDragEnd();
    },
    [draggingId, reorderTab, handleDragEnd],
  );

  if (!tabs || tabs.length === 0) return null;

  return (
    <Flx
      fw
      sx={{
        pl: 1,
        pr: 0.5,
        background: STRIP_BG,
        minHeight: "34px",
        // Tabs sit flush on the strip's bottom edge (browser-chrome look), so
        // their rounded top corners are the only visible rounding.
        alignItems: "flex-end",
        overflowX: "auto",
        overflowY: "hidden",
        "&::-webkit-scrollbar": { height: 4 },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(255,255,255,0.35)",
          borderRadius: 2,
        },
      }}
    >
      <Flx
        g={0.3}
        sx={{
          alignItems: "flex-end",
          flexWrap: "nowrap",
          flex: 1,
          minWidth: 0,
        }}
      >
        {tabs.map((loan) => (
          <LoanTabItem
            key={loan._id}
            loanId={loan._id}
            loanNumber={loan.loanNumber}
            loanName={loan.loanName}
            pipeline={loan.pipeline}
            onRemove={removeTab}
            onRename={renameTab}
            loading={loading}
            isDragging={draggingId === loan._id}
            isDragOver={dragOverId === loan._id && draggingId !== loan._id}
            onDragStartTab={handleDragStart}
            onDragOverTab={handleDragOverTab}
            onDropTab={handleDropOnTab}
            onDragEndTab={handleDragEnd}
          />
        ))}
      </Flx>

      {/* Collapse the strip. Sticky so it stays reachable once the tab row
          scrolls horizontally. */}
      <Tooltip title="Hide pinned tabs" placement="bottom" arrow>
        <IconButton
          size="small"
          onClick={onToggle}
          sx={{
            flexShrink: 0,
            position: "sticky",
            right: 0,
            mb: 0.4,
            ml: 0.5,
            p: 0.4,
            color: "#ffffff",
            borderRadius: "6px",
            background: TAB_IDLE_BG,
            "&:hover": { background: TAB_IDLE_HOVER_BG },
          }}
        >
          <KeyboardArrowUpRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Flx>
  );
};

// The tab's leading glyph doubles as the unpin control: a static pin until the
// cursor is on the glyph itself, then a struck-through pin. Hover is tracked
// here rather than on the tab so the swap is scoped to this button. Fixed-size
// slot in every state so swapping never shifts the tab's contents.
const PIN_SLOT_SX = {
  width: 20,
  height: 20,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const PinSlot = ({ interactive = true, loading, onUnpin }) => {
  // The tooltip is controlled rather than left to MUI's own hover tracking:
  // MUI only closes on mouseleave, and this button sits inside a draggable,
  // horizontally scrollable strip — starting a drag or scrolling the strip
  // moves the anchor out from under the cursor WITHOUT the browser ever
  // firing mouseleave, which left the "Unpin tab" tip stranded on screen.
  const [tipOpen, setTipOpen] = useState(false);
  const tipTimer = useRef(null);

  const closeTip = useCallback(() => {
    clearTimeout(tipTimer.current);
    setTipOpen(false);
  }, []);

  const openTipSoon = useCallback(() => {
    clearTimeout(tipTimer.current);
    // Matches MUI's own enter delay so sweeping across the strip doesn't flash
    // a tooltip on every pinned tab.
    tipTimer.current = setTimeout(() => setTipOpen(true), 400);
  }, []);

  useEffect(() => () => clearTimeout(tipTimer.current), []);

  // The button unmounts while a pin/unpin is in flight and while renaming;
  // drop the hover state with it so it can't come back open on remount.
  useEffect(() => {
    if (loading || !interactive) closeTip();
  }, [loading, interactive, closeTip]);

  // Safety net for every way the anchor can leave the cursor silently.
  useEffect(() => {
    if (!tipOpen) return undefined;
    const close = () => closeTip();
    window.addEventListener("dragstart", close, true);
    window.addEventListener("scroll", close, true);
    window.addEventListener("blur", close);
    document.addEventListener("visibilitychange", close);
    return () => {
      window.removeEventListener("dragstart", close, true);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("blur", close);
      document.removeEventListener("visibilitychange", close);
    };
  }, [tipOpen, closeTip]);

  const pin = <PushPin sx={{ fontSize: 12, transform: "rotate(30deg)" }} />;

  if (loading) {
    return (
      <Box className="tab-icon" sx={PIN_SLOT_SX}>
        <CircularProgress size={12} sx={{ color: "inherit" }} />
      </Box>
    );
  }

  // While renaming, the glyph is decoration only — no accidental unpin.
  if (!interactive) {
    return (
      <Box className="tab-icon" sx={PIN_SLOT_SX}>
        {pin}
      </Box>
    );
  }

  return (
    <Tooltip
      title="Unpin tab"
      placement="bottom"
      arrow
      open={tipOpen}
      disableInteractive
    >
      <IconButton
        size="small"
        className="unpin-btn"
        aria-label="Unpin tab"
        onMouseEnter={openTipSoon}
        onMouseLeave={closeTip}
        // Fires before dragstart, so a drag begun on the pin can't strand the
        // tooltip behind it.
        onMouseDown={closeTip}
        onClick={(e) => {
          e.stopPropagation();
          closeTip();
          onUnpin?.();
        }}
        // The pin ↔ struck-pin swap is pure CSS :hover with both glyphs always
        // mounted and click-through. Swapping them in React state on hover/
        // mousedown replaced the node under the cursor mid-click, so the browser
        // dropped the click and the first press never unpinned.
        sx={{
          ...PIN_SLOT_SX,
          p: 0,
          borderRadius: "6px",
          "& > *": { pointerEvents: "none" },
          "& .pin-off": { display: "none" },
          "&:hover .pin-on": { display: "none" },
          "&:hover .pin-off": { display: "flex" },
        }}
      >
        <Box className="pin-on" sx={{ display: "flex" }}>
          {pin}
        </Box>
        {/* MUI has no "pin off" glyph — outline pin plus a diagonal strike. */}
        <Box className="pin-off" sx={{ position: "relative" }}>
          <PushPinOutlined sx={{ fontSize: 13 }} />
          <Box
            component="span"
            sx={{
              position: "absolute",
              left: -1,
              right: -1,
              top: "50%",
              height: "1.5px",
              borderRadius: "1px",
              background: "currentColor",
              transform: "rotate(-45deg)",
            }}
          />
        </Box>
      </IconButton>
    </Tooltip>
  );
};

const LoanTabItem = ({
  loanId,
  loanNumber,
  loanName,
  pipeline,
  onRemove,
  onRename,
  loading,
  isDragging,
  isDragOver,
  onDragStartTab,
  onDragOverTab,
  onDropTab,
  onDragEndTab,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isEditing, setIsEditing] = useState(false);
  // Hover over the loan label — the only thing that reveals the rename /
  // open-in-new-tab buttons. The pin slot tracks its own hover separately.
  const [isLabelHovered, setIsLabelHovered] = useState(false);
  const [draftName, setDraftName] = useState(loanName || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isEditing) setDraftName(loanName || "");
  }, [loanName, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [isEditing]);

  const isAcquisition = pipelineOf(pipeline) === PIPELINE_KEYS.ACQUISITIONS;
  // "/loan" for underwriting, "/acquisition" for acquisitions.
  const drilldownBase = drilldownBaseFor(pipeline);

  const active = useMemo(() => {
    const segments = (location?.pathname || "").split("/").filter(Boolean);
    // Match this tab's own base segment ("loan" / "acquisition"). Exact segment
    // matching keeps the list routes ("loans" / "acquisitions") from matching.
    const baseSegment = drilldownBase.replace(/^\//, "");
    const baseIndex = segments.indexOf(baseSegment);
    if (baseIndex !== -1 && segments[baseIndex + 1]) {
      return String(loanId) === String(segments[baseIndex + 1]);
    }
    return false;
  }, [location, loanId, drilldownBase]);

  const styles = useMemo(
    () => ({
      // Browser-tab shape: white and lifted when active, a translucent white
      // wash on the blue strip when idle.
      backgroundColor: active ? "#ffffff" : TAB_IDLE_BG,
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      pl: 0.75,
      pr: 1.25,
      py: 0.75,
      display: "flex",
      alignItems: "center",
      // gap: 0.75,
      gap: 0.25,
      flex: "0 1 auto",
      minWidth: 0,
      maxWidth: 260,
      // Drag visuals: the dragged tab fades out; the tab being hovered shows an
      // inset accent line marking where it will be inserted (inset box-shadow
      // instead of a border so nothing shifts).
      opacity: isDragging ? 0.4 : loading ? 0.6 : 1,
      cursor: isEditing ? "default" : "grab",
      boxShadow: isDragOver
        ? `inset 3px 0 0 0 ${active ? BRAND : "#ffffff"}`
        : "none",
      transition: "opacity 0.15s, box-shadow 0.12s, background-color 0.12s",
      "&:hover": active ? undefined : { backgroundColor: TAB_IDLE_HOVER_BG },
      ".tab-icon": {
        flexShrink: 0,
        color: active ? BRAND : "rgba(255,255,255,0.75)",
      },
      ".loan-number": {
        fontSize: "11px",
        fontWeight: 700,
        flexShrink: 0,
        lineHeight: 1.5,
        px: 0.7,
        py: 0.1,
        borderRadius: "6px",
        color: active ? BRAND : "#ffffff",
        // Subtle tint pill — brand wash on the white active tab, a white wash
        // on the blue idle tabs.
        backgroundColor: active
          ? "rgba(31,90,242,0.10)"
          : "rgba(255,255,255,0.18)",
      },
      ".MuiIconButton-root": { color: active ? grey[600] : "#ffffff" },
      // Declared after the generic icon-button rule so it wins on color.
      ".unpin-btn": {
        color: active ? BRAND : "rgba(255,255,255,0.75)",
        "&:hover": {
          color: active ? BRAND : "#ffffff",
          background: active
            ? "rgba(31,90,242,0.12)"
            : "rgba(255,255,255,0.25)",
        },
      },
    }),
    [active, loading, isDragging, isDragOver, isEditing],
  );

  const handleOnClick = useCallback(() => {
    if (isEditing) return;
    // Don't clear the shared loanDrilldown state here. When navigating from an
    // embedded drilldown (e.g. the appraisal queue), nulling it while that
    // screen is still mounted trips its self-heal fetch effect → an extra "Get
    // Loan By ID" for the loan we're leaving. The destination drilldown already
    // clears + fetches when the route id doesn't match the loaded loan, and the
    // screen guards on `_id !== loanId` to show the spinner during the swap.
    navigate(`${drilldownBase}/${loanId}/summary`);
  }, [navigate, loanId, isEditing, drilldownBase]);

  const commitRename = useCallback(() => {
    const trimmed = (draftName || "").trim();
    if (trimmed !== (loanName || "")) {
      onRename?.(loanNumber, trimmed);
    }
    setIsEditing(false);
  }, [draftName, loanName, loanNumber, onRename]);

  const cancelRename = useCallback(() => {
    setDraftName(loanName || "");
    setIsEditing(false);
  }, [loanName]);

  const handleStartEdit = useCallback(
    (e) => {
      e.stopPropagation();
      if (loading) return;
      setIsEditing(true);
    },
    [loading],
  );

  const handleOpenInNewTab = useCallback(
    (e) => {
      e.stopPropagation();
      window.open(
        `${drilldownBase}/${loanId}/summary`,
        "_blank",
        "noopener,noreferrer",
      );
    },
    [loanId, drilldownBase],
  );

  return (
    <Box
      sx={styles}
      // Native HTML5 drag-to-reorder. Disabled while renaming so the text input
      // stays selectable.
      draggable={!isEditing && !loading}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        // Firefox requires data to be set for a drag to actually start.
        try {
          e.dataTransfer.setData("text/plain", String(loanId));
        } catch {
          // ignore — some browsers restrict setData outside user gestures
        }
        // A native drag stops mouse events, so the label-hover flag (and any
        // tooltip riding on it) would otherwise stay stuck open past the drop.
        setIsLabelHovered(false);
        onDragStartTab?.(loanId);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOverTab?.(loanId);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDropTab?.(loanId);
      }}
      onDragEnd={() => onDragEndTab?.()}
    >
      {/* Pin slot — a plain glyph until the cursor is on it, then the unpin
          control. Lives outside the navigating click area so unpinning never
          doubles as a navigation. */}
      <PinSlot
        interactive={!isEditing}
        loading={loading}
        onUnpin={() => onRemove(loanNumber)}
      />
      {/* Label zone — the open-in-new-tab button only surfaces while the
          cursor is over the loan label, and lives inside this same zone so
          moving toward it doesn't dismiss it. Rename is in the name tooltip. */}
      <Flx
        ac
        sx={{ flex: 1, minWidth: 0, position: "relative" }}
        onMouseEnter={() => setIsLabelHovered(true)}
        onMouseLeave={() => setIsLabelHovered(false)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            cursor: isEditing ? "default" : "pointer",
            flex: 1,
            minWidth: 0,
          }}
          onClick={handleOnClick}
        >
          {/* Acquisition loans live in a different pipeline (/acquisition) — flag
            them so a pinned tab makes the loan's type obvious at a glance. */}
          {isAcquisition && (
            <Tooltip title="Acquisition loan" placement="bottom" arrow>
              <Box
                sx={{
                  flexShrink: 0,
                  px: 0.5,
                  borderRadius: "4px",
                  background: "#faeeda",
                  border: "1px solid #f5c97a",
                  color: "#854f0b",
                  fontSize: "8.5px",
                  fontWeight: 700,
                  lineHeight: "13px",
                  letterSpacing: "0.03em",
                }}
              >
                ACQ
              </Box>
            </Tooltip>
          )}
          <Box component="span" className="loan-number">
            #{loanNumber}
          </Box>
          {isEditing ? (
            <InputBase
              inputRef={inputRef}
              value={draftName}
              placeholder="Tab name"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelRename();
                }
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: "150px",
                fontSize: "11px",
                fontWeight: 600,
                color: BRAND,
                lineHeight: 1.5,
                px: 0.4,
                border: `1px solid ${grey[300]}`,
                borderRadius: "3px",
                background: "#fff",
                "& input": { p: 0, fontSize: "11px", fontWeight: 600 },
              }}
            />
          ) : (
            // Rename lives in the name tooltip (interactive so the cursor can
            // reach it), keeping the tab itself down to one hover action. The
            // theme defaults every tooltip to disableInteractive, so it's
            // switched back on here; the leave delay bridges the gap between
            // the label and the popper so crossing it doesn't close the tip.
            <Tooltip
              placement="bottom"
              arrow
              disableInteractive={false}
              leaveDelay={150}
              title={
                <Flx ac g={0.75}>
                  <Box
                    component="span"
                    sx={{ fontStyle: loanName ? "normal" : "italic" }}
                  >
                    {loanName || "Unnamed tab"}
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="Rename tab"
                    title="Rename tab"
                    onClick={handleStartEdit}
                    disabled={loading}
                    sx={{
                      p: 0.25,
                      color: "#ffffff",
                      borderRadius: "4px",
                      "&:hover": { background: "rgba(255,255,255,0.18)" },
                    }}
                  >
                    <EditOutlined sx={{ fontSize: 13 }} />
                  </IconButton>
                </Flx>
              }
            >
              <Typography
                className="txt"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  lineHeight: 1.5,
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "150px",
                  fontSize: "11px",
                  fontWeight: active ? 600 : 500,
                  color: loanName
                    ? active
                      ? "#1e293b"
                      : "#ffffff"
                    : active
                      ? grey[500]
                      : "rgba(255,255,255,0.6)",
                  fontStyle: loanName ? "normal" : "italic",
                }}
              >
                {loanName || "Rename"}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {/* Overlaid on the label's right end (browser-tab style) rather than
            laid out after it, so revealing it never widens the tab. The
            backdrop is the tab's own colour made opaque — the idle wash is
            translucent, so it's layered over the strip colour — and masked to
            fade in over the text it covers. */}
        {!isEditing && isLabelHovered && (
          <Flx
            ac
            g={0.5}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              right: 0,
              pl: 1.5,
              background: active
                ? "#ffffff"
                : `linear-gradient(${TAB_IDLE_HOVER_BG}, ${TAB_IDLE_HOVER_BG}), ${STRIP_BG}`,
              maskImage: "linear-gradient(to right, transparent, #000 12px)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent, #000 12px)",
            }}
          >
            <Tooltip title="Open in new tab" placement="bottom" arrow>
              <IconButton
                size="small"
                sx={{ p: 0 }}
                onClick={handleOpenInNewTab}
                disabled={loading}
              >
                <OpenInNewOutlined className="thin" sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Flx>
        )}
      </Flx>
    </Box>
  );
};

export default WorkspaceTabs;
